/**
 * The pieces a stone is cut into (claude-docs/2026-10-15-slab-packing-design.md §1):
 * a top's length by its depth, each waterfall leg, a corner kite's bounding
 * rectangle in its OWN orientation, and a designed backsplash's rectangle on
 * its wall. Inches. Pure.
 */
const { waterfallLegs } = require('./countertopFabrication');
const { stoneBacksOf } = require('../../../cabinet-runs/stoneBack');
const { footprintFor } = require('../../../geometry/objectFootprint');
const { isSlabFinish } = require('../slabFinish');
const { cutStationsOf } = require('./seamPlacement');

// Inches to the nearest 1/16" — how a fabricator measures a piece, and what
// keeps a design-made 120" top (3.048 m) from reading 120.00006" and
// spilling onto a second slab.
const toFabricationInches = (m) => Math.round((m / 0.0254) * 16) / 16;

const isPolygonTop = (top) =>
  Array.isArray(top?.polygonCorners) && top.polygonCorners.length >= 3;

// The smallest rectangle around a polygon, over the directions of its own
// edges (the minimum-area box of a convex polygon lies along one of them).
// Points are `{x, z}` in metres; returns `{ longIn, shortIn }`.
const ownBoundingSides = (points) => {
  let best = null;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    const len = Math.hypot(b.x - a.x, b.z - a.z);
    if (!(len > 0)) continue;
    const ux = (b.x - a.x) / len;
    const uz = (b.z - a.z) / len;
    let u0 = Infinity;
    let u1 = -Infinity;
    let v0 = Infinity;
    let v1 = -Infinity;
    for (const p of points) {
      const u = p.x * ux + p.z * uz;
      const v = -p.x * uz + p.z * ux;
      u0 = Math.min(u0, u);
      u1 = Math.max(u1, u);
      v0 = Math.min(v0, v);
      v1 = Math.max(v1, v);
    }
    const w = u1 - u0;
    const h = v1 - v0;
    if (!best || w * h < best.w * best.h) best = { w, h, ux, uz };
  }
  if (!best) return null;
  // the direction of the long side, in the same plan frame as the points
  const along =
    best.w >= best.h ? { x: best.ux, z: best.uz } : { x: -best.uz, z: best.ux };
  return {
    longIn: toFabricationInches(Math.max(best.w, best.h)),
    shortIn: toFabricationInches(Math.min(best.w, best.h)),
    along,
  };
};

const sidesIn = (aM, bM) => ({
  longIn: toFabricationInches(Math.max(aM, bM)),
  shortIn: toFabricationInches(Math.min(aM, bM)),
});

// A waterfall leg is drawn on the plan as a strip this thick, inset into the
// end of its top (a 1.5" slab edge).
const LEG_STRIP_M = 0.0381;

const plainPoints = (points) => points.map((p) => ({ x: p.x, z: p.z }));

/**
 * Where a top lies on the floor plan (plan metres, world x / z): its four
 * rotated corners from `footprintFor` — the repo's one rotated-footprint
 * primitive, which takes `rotation.y` in radians and puts local +X at
 * (cos ry, -sin ry) — and `along`, the unit vector of its length: local +X, or
 * the depth axis when the top is deeper than long. Null when it cannot be
 * placed.
 */
function topPlan(top) {
  const w = top.dimensions.x;
  const d = top.dimensions.z;
  const outline = footprintFor(top.center, w, d, top.rotation?.y);
  if (!outline) return null;
  const ry = top.rotation?.y || 0;
  const alongX = { x: Math.cos(ry), z: -Math.sin(ry) };
  const alongZ = { x: Math.sin(ry), z: Math.cos(ry) };
  return {
    outline: plainPoints(outline),
    along: d > w ? alongZ : alongX,
  };
}

/**
 * The strip a waterfall leg is drawn as at one end of its top: the end edge,
 * `LEG_STRIP_M` thick, inset into the top. Its length runs across the top's
 * depth. `end` is `'left'` (local -X) or `'right'` (local +X).
 */
function legPlan(top, end, dropM) {
  const w = top.dimensions.x;
  const d = top.dimensions.z;
  const ry = top.rotation?.y || 0;
  const dir = { x: Math.cos(ry), z: -Math.sin(ry) };
  const sign = end === 'left' ? -1 : 1;
  const inset = w / 2 - LEG_STRIP_M / 2;
  const center = {
    x: top.center.x + sign * inset * dir.x,
    z: top.center.z + sign * inset * dir.z,
  };
  const outline = footprintFor(center, LEG_STRIP_M, d, ry);
  if (!outline) return null;
  return {
    outline: plainPoints(outline),
    along: { x: Math.sin(ry), z: Math.cos(ry) },
    // the leg's length is its drop when that is the longer side — the plan
    // shows only its depth
    alongIsLength: d >= dropM,
    // a thin strip's tag goes out past the end the leg drops at
    outward: { x: sign * dir.x, z: sign * dir.z },
  };
}

/**
 * Where a stone back lies on the floor plan: the panel's own footprint, a thin
 * strip along its length, its tag set out past its finished face (local +Z).
 */
function backPlan(back) {
  const ry = back.rotation?.y || 0;
  const outline = footprintFor(
    back.center,
    back.dimensions.x,
    back.dimensions.z,
    ry
  );
  if (!outline) return null;
  return {
    outline: plainPoints(outline),
    along: { x: Math.cos(ry), z: -Math.sin(ry) },
    alongIsLength: true,
    outward: { x: Math.sin(ry), z: Math.cos(ry) },
  };
}

const hasCenter = (top) =>
  typeof top?.center?.x === 'number' && typeof top?.center?.z === 'number';

/**
 * A countertop's FRONT — its own length (local x), the edge the cabinets
 * face and the one a prefabricated slab has finished — and its depth,
 * whichever is longer: a 3" top between a corner and a range is 3" of front
 * and 25" deep, and is cut as 3" of a slab's length (2026-10-20; before, its
 * 25" depth was laid along the slab). Only a `countertop`: a tub deck's
 * sides say nothing about which edge is its front.
 */
const frontOf = (top) =>
  top.type === 'countertop'
    ? {
        frontIn: toFabricationInches(top.dimensions.x),
        depthIn: toFabricationInches(top.dimensions.z),
      }
    : {};

/**
 * Where a top lies, as the rectangle it is: plan centre (metres), its
 * rotation, and its length (local x) and depth (local z). What a corner
 * re-cut (`countertopCorners.js`) moves.
 */
const frameOf = (top) => ({
  x: top.center.x,
  z: top.center.z,
  ry: top.rotation?.y || 0,
  lengthM: top.dimensions.x,
  depthM: top.dimensions.z,
});

/**
 * A top piece cut to a new length along its own run, `shiftM` its centre
 * moved along that run — the piece a fabricator cuts when a corner square
 * goes to the other top (`countertopCorners.js`). Its sides, front and plan
 * follow; everything else about it (id, room) is kept. Pure.
 */
function recutTop(piece, lengthM, shiftM) {
  const { frame } = piece;
  const dir = { x: Math.cos(frame.ry), z: -Math.sin(frame.ry) };
  const moved = {
    ...frame,
    x: frame.x + dir.x * shiftM,
    z: frame.z + dir.z * shiftM,
    lengthM,
  };
  const asTop = {
    center: { x: moved.x, z: moved.z },
    dimensions: { x: lengthM, z: frame.depthM },
    rotation: { y: frame.ry },
  };
  return {
    ...piece,
    ...sidesIn(lengthM, frame.depthM),
    ...(piece.frontIn != null ? { frontIn: toFabricationInches(lengthM) } : {}),
    frame: moved,
    plan: topPlan(asTop),
  };
}

/**
 * A billed top's pieces: the top (or kite), then one per waterfall leg. Each
 * carries `plan` (`{ outline, along }`, plan metres) so the room plan can draw
 * where it goes, or null when it cannot be placed.
 */
function topPieces(top, ctx) {
  if (isPolygonTop(top)) {
    const sides = ownBoundingSides(top.polygonCorners);
    if (!sides) return [];
    const { along, ...rest } = sides;
    return [
      {
        id: top.id,
        kind: 'kite',
        ...rest,
        plan: { outline: plainPoints(top.polygonCorners), along },
      },
    ];
  }
  const length = top?.dimensions?.x;
  const depth = top?.dimensions?.z;
  if (!(length > 0) || !(depth > 0)) return [];
  const placed = hasCenter(top);
  const pieces = [
    {
      id: top.id,
      kind: 'top',
      ...sidesIn(length, depth),
      ...frontOf(top),
      ...(placed ? { frame: frameOf(top) } : {}),
      // where a seam may go if the top must be cut (`seamPlacement.js`)
      ...(placed && top.type === 'countertop'
        ? { cutStations: cutStationsOf(top, ctx?.objects) }
        : {}),
      plan: placed ? topPlan(top) : null,
    },
  ];
  const legs = waterfallLegs(top, ctx);
  const legEnds = ['left', 'right'].filter((end) => legs?.ends?.[end]);
  for (let i = 0; i < (legs?.legs ?? 0); i++) {
    pieces.push({
      id: `${top.id}#leg${i + 1}`,
      kind: 'leg',
      ...sidesIn(legs.dropM, depth),
      plan:
        placed && legEnds.length === legs.legs
          ? legPlan(top, legEnds[i], legs.dropM)
          : null,
    });
  }
  // a peninsula back cut from this top's stone (`stoneBacksOf`): one panel,
  // its length by its height
  stoneBacksOf(top, ctx || {}).forEach((back, i) => {
    pieces.push({
      id: `${top.id}#back${i + 1}`,
      kind: 'back',
      ...sidesIn(back.dimensions.x, back.dimensions.y),
      plan: hasCenter(back) ? backPlan(back) : null,
    });
  });
  return pieces;
}

const BACKSPLASH_SURFACES = new Set(['backsplash', 'tubBacksplash']);

/** A wall region the design made a backsplash (`backsplashRegion.js`). */
const isBacksplashRegion = (region) =>
  BACKSPLASH_SURFACES.has(region?.surfaceType);

// A backsplash is drawn on the plan as a strip this thick on the room side of
// its wall (a 1.5" slab).
const BACKSPLASH_STRIP_M = 0.0381;

/**
 * A backsplash's strip on the plan: along its wall from `rect.a0` to
 * `rect.a1`, `BACKSPLASH_STRIP_M` thick on the room side. `frame` is the
 * wall's (`wallPlanFrame`): the plan point where `a = 0` is, the unit
 * direction of increasing `a`, and the unit normal pointing into the room.
 */
function backsplashPlan(rect, frame, alongIsLength) {
  const at = (a, off) => ({
    x: frame.start.x + frame.dir.x * a + frame.normal.x * off,
    z: frame.start.z + frame.dir.z * a + frame.normal.z * off,
  });
  return {
    outline: [
      at(rect.a0, 0),
      at(rect.a1, 0),
      at(rect.a1, BACKSPLASH_STRIP_M),
      at(rect.a0, BACKSPLASH_STRIP_M),
    ],
    along: { x: frame.dir.x, z: frame.dir.z },
    alongIsLength,
    // a thin strip's tag goes out into the room
    outward: { x: frame.normal.x, z: frame.normal.z },
  };
}

/**
 * A backsplash region's piece: its rectangle on the wall (metres, the
 * `regionRectOnWall` frame) — shortened to the area it was billed for where a
 * region in front of it covers part of it — or null for any other region.
 * With the wall's plan `frame` (or none: `plan` is null) it also says where
 * it stands in the room.
 */
function backsplashPiece(region, rect, billedAreaSqM, id, frame = null) {
  if (!isBacksplashRegion(region)) return null;
  const along = rect.a1 - rect.a0;
  const height = rect.y1 - rect.y0;
  if (!(along > 0) || !(height > 0) || !(billedAreaSqM > 0)) return null;
  const billedAlong = Math.min(along, billedAreaSqM / height);
  return {
    id,
    kind: 'backsplash',
    longIn: toFabricationInches(billedAlong),
    shortIn: toFabricationInches(height),
    plan: frame ? backsplashPlan(rect, frame, billedAlong >= height) : null,
  };
}

module.exports = {
  topPieces,
  recutTop,
  backsplashPiece,
  isBacksplashRegion,
  // re-exported from `../slabFinish`, its home, so existing imports keep working
  isSlabFinish,
};
